import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';

import { terminal } from '../app/terminal.js';

/**
 * The login screen.
 *
 * Three fields, in the order a trader knows them: broker (tenant), login,
 * password. The **tenant is resolved at the edge** (docs/10 §3.2): the browser
 * sends `X-Tenant` and the server signs the resolved id into the token, so the
 * field is a broker name, not a database key, and it cannot select another
 * tenant after sign-in.
 *
 * The demo credentials are shown as a hint because this is a demo stack and the
 * acceptance criterion is a non-technical tester signing in unaided. A production
 * build is expected to remove that line (`docs/12-p5-handoff.md`).
 */
export function Login(): ReactElement {
  const [tenant, setTenant] = useState('demo');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await terminal.login(tenant.trim(), login.trim(), password);
    setBusy(false);
    if (!result.ok) setError(result.message);
  };

  return (
    <main className="flex h-full items-center justify-center bg-bg p-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-4">
          <p className="text-2xs tracking-[0.3em] text-accent uppercase">Weke Forex</p>
          <h1 className="text-lg font-semibold">Trader terminal</h1>
          <p className="muted text-2xs">
            Sign in with the credentials your broker issued. The broker name chooses the tenant.
          </p>
        </div>
        <form
          className="panel gap-2 p-3"
          onSubmit={(event) => void submit(event)}
          aria-label="Sign in"
          data-testid="login-form"
        >
          <label className="flex flex-col gap-0.5">
            <span className="label">Broker (tenant)</span>
            <input
              className="field text-left"
              value={tenant}
              onChange={(event) => setTenant(event.target.value)}
              autoComplete="organization"
              required
              data-testid="login-tenant"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="label">Login</span>
            <input
              className="field text-left"
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              inputMode="numeric"
              autoComplete="username"
              required
              autoFocus
              data-testid="login-login"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="label">Password</span>
            <input
              className="field text-left"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              data-testid="login-password"
            />
          </label>
          {error === null ? null : (
            <p className="text-2xs text-sell" role="alert" data-testid="login-error">
              {error}
            </p>
          )}
          <button type="submit" className="btn btn-primary mt-1" disabled={busy} data-testid="login-submit">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="faint mt-3 text-2xs">
          Demo stack: broker <span className="num">demo</span>, login <span className="num">9001</span>,
          password <span className="num">demo-pass-123</span>. Provisioned by{' '}
          <span className="num">pnpm demo:provision</span>.
        </p>
      </div>
    </main>
  );
}
